import React, { useEffect, useMemo, useState } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { FadeUp } from '../components/MotionReveals';
import {
  Gauge, AlertTriangle, ClipboardCheck, RotateCcw, Eye, CheckCircle2,
  ChevronRight, Film, Clock, HelpCircle, Quote, ListChecks, Route, Truck,
} from 'lucide-react';

// ─── Données pédagogiques (analyse vidéo giratoire hors agglo) ───
const VIDEOS = {
  'pl-moto': {
    tab: 'Vidéo 1 – PL + Moto',
    badge: 'Renault hors agglo · 44 s · 60→22 km/h · PL long + moto',
    titre: 'Giratoire hors agglo — PL + Moto',
    videoSrc: '/videos/giratoire/video1-pl-moto.mp4',
    chapters: [
      { t: 0, label: 'Panneau A15 · 150 m', desc: 'Annonce giratoire, moto devant, on garde distance' },
      { t: 5, label: 'GPS 46 km/h', desc: 'On commence à lâcher l\'accélérateur' },
      { t: 9, label: 'Triangle AB25 · Cédez', desc: 'Contrôle rétro gauche' },
      { t: 14, label: 'Voiture grise + camion', desc: 'Déjà dans l\'anneau = on cède' },
      { t: 19, label: 'Angle mort moto', desc: 'Vérifié, on s\'engage voie extérieure' },
      { t: 28, label: 'Cligno droit AVANT sortie', desc: 'On sort sans couper le camion' },
    ],
    dangerNote: 'Toujours vérifier angles morts gauche/droite. La moto de la vidéo reste devant jusqu\'à 15 s, ne la collez pas.',
    focus: {
      txt: <>À 14 s tu vois la voiture grise à gauche dans l'anneau + le camion déjà engagé. Tu as bien cédé. C'est exactement l'erreur que font <strong className="text-white">70 % des élèves</strong> : s'engager en même temps qu'un poids-lourd. Garde 1 anneau d'écart avec les camions.</>,
      tags: ['Angle mort moto obligatoire', 'Ne jamais couper la trajectoire d\'un camion'],
    },
    quiz: [
      { q: 'À 19 s, qui est prioritaire ?', opts: ['Toi, car tu arrives par la droite', 'Les véhicules déjà dans le giratoire (voiture grise à gauche)', 'Le camion derrière toi'], ok: 1,
        exp: 'En giratoire à la française, tu cèdes à gauche à ceux déjà engagés dans l\'anneau. Pas de priorité à droite ici.' },
      { q: 'Quand mettre le clignotant droit pour sortir ?', opts: ['Dès l\'entrée du giratoire', 'Juste après la sortie qui précède la mienne', 'Jamais, c\'est un giratoire'], ok: 1,
        exp: 'Cligno droit uniquement après la sortie N-1 : avant, tu induis les autres en erreur.' },
      { q: 'Faut-il s\'arrêter au cédez-le-passage si la voie est libre ?', opts: ['Oui, arrêt obligatoire comme un STOP', 'Non, on cède mais on s\'engage si libre, sans s\'arrêter', 'Oui, 3 secondes minimum'], ok: 1,
        exp: 'Le cédez n\'impose pas l\'arrêt : on garde du roulement si l\'anneau est libre, c\'est même valorisé à l\'examen.' },
    ],
    curve: [60, 60, 59, 48, 46, 40, 32, 22, 19, 17, 20, 22, 26, 32, 29, 28, 31, 32, 37, 44],
    stats: [{ v: 60, l: 'km/h approche' }, { v: 22, l: 'km/h entrée' }, { v: 40, l: 'km/h sortie' }],
    identite: {
      type: 'Grand giratoire 2 voies hors agglo',
      typeDesc: 'Anneau large, visibilité dégagée, 70 → 50 avant panneau.',
      difficulte: 'Intermédiaire',
      difficulteDesc: 'Gestion multi-usagers + gabarit.',
      danger: 'Gabarit PL + usager vulnérable moto',
      dangerDesc: 'PL qui empiète et moto qui se faufile à droite.',
      chips: ['🚗 Renault · Boîte manuelle · Vue capot', '📍 Hors agglo · D42b + AB3a · 150 m', '🚦 Trafic : voiture + moto + PL long'],
    },
    comprendre: <>Un giratoire hors agglo n'est pas une priorité : c'est un <strong className="text-white">cédez-le-passage permanent</strong>. La bonne allure (22 km/h à l'entrée) permet d'observer sans s'arrêter.</>,
    vitesseLegale: '80 → 50 → 30',
    rapport: '3ème → 2ème',
    remc: 'Réf : REMC C2 — Gérer les intersections complexes',
    segments: [
      { time: '00:00 – 00:08', speed: '60 → 46 km/h', title: 'D42b à 150 m', emoji: '👁️',
        obs: ['Panneau A21 annonce giratoire à 150 m visible à 6 s', 'Panneau D42b directionnel + marquage qui se resserre', 'Lecture trafic lointain : voiture + moto + PL dans l\'anneau', 'Lever pied anticipé, pas de freinage brutal'],
        q: 'Quelle sortie on prend ? Quelle voie ?',
        r: 'On reste en voie de droite si on va tout droit / 1ère ou 2ème sortie. Lecture panneaux bleus avant. Pas de cligno à l\'approche si on ne prend pas la 1ère.',
        verdict: 'Stabilisé 60 km/h puis lever pied. Objectif 40 km/h au AB3a.' },
      { time: '00:08 – 00:15', speed: '40 → 22 km/h', title: 'Cédez le passage', emoji: '🐢',
        obs: ['Rétro intérieur + rétro gauche (moto derrière ?)', 'Panneau AB3a + triangles cédez + ligne pointillée', 'Moto devant qui ralentit aussi : distance de sécurité', 'Dégradé : 46 → 40 → 32 → 22 km/h en 2ème'],
        q: 'On s\'arrête ou on s\'insère ?',
        r: 'On garde du roulement si possible. Ici on cède au PL long déjà engagé. 2ème à ~20 km/h, moto gardée à vue.',
        verdict: 'Freinage dégressif, 22 km/h à l\'entrée = bonne gestion allure.' },
      { time: '00:15 – 00:28', speed: '17 → 32 km/h', title: 'PL + Moto = danger', emoji: '🚛',
        obs: ['PL semi qui empiète sur voie extérieure (gabarit + porte-à-faux)', 'Moto qui se faufile à droite du PL (masquée)', 'Anneau 2 voies, 17-28 km/h, rester extérieur si sortie proche', 'Ne jamais se mettre à hauteur d\'un PL'],
        q: 'Je me place à côté du PL ?',
        r: 'NON. Jamais. Angle mort total. On reste derrière, extérieur, 2 s d\'intervalle. On surveille la moto qui peut se rabattre.',
        verdict: '17 km/h au cœur puis 26-32 km/h en sortie d\'anneau, fluide.' },
      { time: '00:28 – 00:44', speed: '20 → 44 km/h', title: 'Cligno + relance', emoji: '💨',
        obs: ['Cligno droit dès qu\'on a passé la sortie précédente', 'Angle mort droit + contrôle piéton/vélo sur trottoir', 'Sortie vers 32-35 s, voie large', 'Relance 20 → 28 → 32 → 40 km/h progressive'],
        q: 'Quand je mets le cligno ?',
        r: 'À la sortie N-1, pas avant. Ici après PL. Angle mort droit systématique puis sortie et 3ème.',
        verdict: 'Relance souple, pas d\'écrasement, retour 40 km/h.' },
    ],
    pieges: [
      { t: 'Ne pas doubler le PL dans l\'anneau', d: 'PL long prend 1,5 voie. À l\'intérieur tu te fais emporter. Reste derrière, extérieur.', tag: 'Gabarit + angle mort' },
      { t: 'Moto qui s\'insère à droite = danger', d: '11-14 s : moto se faufile entre voiture et PL. Si non contrôlée, collision à la sortie.', tag: 'Vulnérable + insertion' },
      { t: 'Ne pas couper la voie du PL', d: 'PL sort large 18-22 s. Couper = latérale. Laisse finir son virage.', tag: 'Trajectoire PL' },
    ],
    conduiteModele: <>Dans cette vidéo, le conducteur modèle <strong className="text-white">ne force pas le passage</strong>. Il laisse le PL long sortir en premier (19-22 s), garde la moto à vue, reste en extérieur et ne met son cligno qu'à 28 s.</>,
    grille: [
      { l: 'Panneau giratoire D42b à 150 m perçu', c: 'Prise d\'info lointaine' },
      { l: 'Ralentissement progressif 60 → 22 km/h', c: 'Gestion allure' },
      { l: 'Rétros intérieur + gauche avant entrée', c: 'Contrôles' },
      { l: 'Pas de cligno à l\'entrée (tout droit)', c: 'Signalisation' },
      { l: 'Cédez-le-passage respecté (PL prioritaire)', c: 'Règle' },
      { l: 'Refus de se placer à côté du PL', c: 'Sécurité gabarit' },
      { l: 'Surveillance moto dans anneau', c: 'Vulnérable' },
      { l: 'Voie extérieure tenue (sortie proche)', c: 'Positionnement' },
      { l: 'Cligno droite après sortie précédente', c: 'Signalisation' },
      { l: 'Angle mort droit + vélo/piéton à la sortie', c: 'Contrôles sortie' },
      { l: 'Relance 20 → 40 km/h progressive', c: 'Allure sortie' },
    ],
    phrase: '« Laisse le PL prendre large, garde la moto à vue, tu restes dehors. »',
  },
  'trafic-dense': {
    tab: 'Vidéo 2 – Trafic dense + PL benne',
    badge: 'Renault hors agglo · 21 s · 0→63 km/h · PL benne GALLOT ZOT · trafic dense',
    titre: 'Giratoire saturé — Trafic dense + PL benne',
    videoSrc: '/videos/giratoire/video2-trafic-dense.mp4',
    chapters: [
      { t: 0, label: 'Arrêt 0 km/h', desc: 'Cédez-le-passage saturé' },
      { t: 6, label: 'Insertion', desc: 'Créneau derrière VL blanc' },
      { t: 11, label: 'Entrée derrière PL benne', desc: 'Gabarit + masque visuel' },
      { t: 14, label: 'Sortie', desc: '2ème sortie, cligno + angle mort' },
    ],
    dangerNote: 'Le PL benne masque tout le champ visuel gauche entre 8 et 12 s : ne jamais démarrer tant que son gabarit complet n\'a pas dégagé la vue.',
    focus: {
      txt: <>À 8 s le PL benne masque toute la vue à gauche. L'erreur classique : démarrer dès que l'avant du PL est passé. On attend que <strong className="text-white">tout le gabarit dégage le champ visuel</strong> avant de s'insérer dans le trou derrière lui.</>,
      tags: ['Attendre un créneau > 6 s', 'Jamais à hauteur d\'un PL'],
    },
    quiz: [
      { q: 'Le PL benne arrive à gauche et masque la vue : que fais-tu ?', opts: ['Je m\'insère vite avant lui', 'Je reste arrêté tant qu\'il n\'a pas dégagé tout le champ visuel', 'Je m\'avance à sa hauteur pour mieux voir'], ok: 1,
        exp: 'Tant que le gabarit masque l\'anneau, tu ne vois pas les véhicules derrière lui. On attend qu\'il dégage toute la vue.' },
      { q: 'Giratoire saturé : quand t\'engages-tu ?', opts: ['Je force le passage, je suis prioritaire', 'J\'attends un vrai créneau (> 6 s) derrière le PL', 'Je klaxonne pour prévenir que j\'arrive'], ok: 1,
        exp: 'En flux dense, forcer = blocage de l\'anneau + infraction. Savoir attendre fait partie de la conduite autonome.' },
      { q: 'Après un arrêt complet à 0 km/h, tu repars en…', opts: ['2ème directement', '1ère, puis 2ème dans l\'anneau', '3ème pour relancer vite'], ok: 1,
        exp: 'À 0 km/h on repart toujours en 1ère (sinon calage), puis 2ème à ~24 km/h dans l\'anneau.' },
    ],
    curve: [3, 0, 0, 0, 0, 3, 8, 0, 0, 24, 24, 26, 31, 41, 48, 63, 63, 63, 60, 55],
    stats: [{ v: 0, l: 'km/h arrêt cédez' }, { v: 24, l: 'km/h insertion' }, { v: 63, l: 'km/h relance' }],
    identite: {
      type: 'Grand giratoire hors agglo saturé · 2 voies',
      typeDesc: 'Flux continu, îlot franchissable, visibilité dégagée mais chargée.',
      difficulte: 'Difficile',
      difficulteDesc: 'Trafic saturé + gabarit PL benne qui masque.',
      danger: 'PL benne GALLOT qui masque + flux continu',
      dangerDesc: '12 m de gabarit, angle mort total, créneau à attendre.',
      chips: ['🚗 Renault · Boîte manuelle · Vue capot', '📍 AB3a + flux dense · 0 km/h', '🚦 Trafic : PL benne GALLOT ZOT + SUV'],
    },
    comprendre: <>Dans un giratoire <strong className="text-white">saturé</strong>, savoir s'arrêter et attendre un vrai créneau fait partie de la conduite autonome. Quand un PL benne masque la vue, <strong className="text-white">on ne démarre pas</strong> tant qu'il n'a pas dégagé tout le champ visuel.</>,
    vitesseLegale: '80 → 50 → 30',
    rapport: '1ère → 2ème → 3ème',
    remc: 'Réf : REMC C2 — Gérer les intersections complexes',
    segments: [
      { time: '00:00 – 00:06', speed: '3 → 0 km/h', title: 'Arrêt au cédez', emoji: '🛑',
        obs: ['Giratoire déjà chargé : SUV blanc + berline grise engagés', 'Marquage cédez + îlot franchissable, visibilité dégagée mais flux continu', 'Arrêt complet à 1-2 s, vitesse 0 km/h au tableau = gestion arrêt', 'Rétro gauche + contrôle moto/vélo avant de s\'avancer'],
        q: 'Je force ou j\'attends ?',
        r: 'On attend. Giratoire saturé hors agglo = on n\'entre pas si on va gêner. Ici on s\'arrête à la ligne, 1ère, on observe gauche, on laisse passer le flux.',
        verdict: '0 km/h stabilisé, pied sur frein, 1ère prête. Bonne gestion arrêt/redémarrage.' },
      { time: '00:06 – 00:12', speed: '0 → 8 km/h', title: 'PL benne GALLOT', emoji: '🚚',
        obs: ['PL benne blanche GALLOT / ZOT qui arrive à 8 s, gabarit énorme', 'PL masque toute la vue à gauche : ne pas démarrer', 'Peugeot 208 noire + fourgon qui s\'insèrent aussi', 'Distance latérale < 1 m avec PL si on se met à côté'],
        q: 'Le PL est loin, je peux passer ?',
        r: 'NON. PL benne = 12 m + angle mort. Il prend toute la largeur. On reste arrêté, on le laisse passer. Il empiète sur 2 voies à l\'entrée.',
        verdict: '8 km/h d\'amorce puis re-freinage à 0. On ne colle pas au PL.' },
      { time: '00:12 – 00:16', speed: '24 → 31 km/h', title: 'Insertion après PL', emoji: '🐢',
        obs: ['Dès que PL dégage à 12-13 s, trou derrière : on s\'insère en 2ème à 24 km/h', 'Contrôle angle mort gauche + rétro intérieur avant insertion', 'Anneau libre 2 voies, on reste extérieur', 'Ne pas accélérer fort dans l\'anneau, rester fluide'],
        q: 'Quelle vitesse dans l\'anneau ?',
        r: '24-31 km/h max. On ne dépasse pas 30 dans ce giratoire. On reste en 2ème, on ne cherche pas à doubler. Visibilité retrouvée après PL.',
        verdict: '24 km/h à l\'insertion, 26-31 km/h dedans, allure sécurisante.' },
      { time: '00:16 – 00:21', speed: '31 → 63 km/h', title: 'Relance hors agglo', emoji: '💨',
        obs: ['Cligno droit dès qu\'on dépasse la sortie avant la nôtre (16 s)', 'Angle mort droit + contrôle trottoir : piéton/vélo possible', 'Sortie large, pas de marquage vélo mais vigilance', 'Relance 31 → 41 → 63 km/h, passage 3ème puis 4ème'],
        q: 'Quand relancer ?',
        r: 'Dès que roues droites en sortie. Ici on est hors agglo, on remonte à 80. Mais progressif, pas d\'écrasement.',
        verdict: '41 km/h à 19 s, 63 km/h à 20-21 s = relance maîtrisée.' },
    ],
    pieges: [
      { t: 'PL benne qui masque la vue', d: 'GALLOT ZOT 8-11 s cache tout l\'anneau. Si tu pars avec lui à côté, tu ne vois plus rien à gauche.', tag: 'Masque + gabarit' },
      { t: 'Forcer dans flux dense', d: 'À 0-6 s flux continu. Forcer = blocage anneau + infraction. Attendre un vrai créneau > 6 s.', tag: 'Trafic saturé' },
      { t: 'Démarrage trop lent en 2ème', d: 'Après arrêt, rester en 2ème trop longtemps = calage. Repartir en 1ère si 0 km/h.', tag: 'Gestion boîte' },
    ],
    conduiteModele: <>Le conducteur modèle <strong className="text-white">s'arrête franchement</strong> au cédez, laisse le PL benne dégager toute la vue, puis s'insère à 24 km/h dans le trou derrière lui — sans jamais se placer à côté du gabarit.</>,
    grille: [
      { l: 'Arrêt marqué au cédez si flux dense', c: 'Règle saturé' },
      { l: 'Refus de s\'insérer à côté du PL benne', c: 'Sécurité gabarit' },
      { l: 'Attente trou > 6 s après PL', c: 'Prise d\'info' },
      { l: 'Surveillance des insérants (208 + fourgon)', c: 'Vulnérable' },
      { l: 'Insertion à 24 km/h en 2ème fluide', c: 'Allure' },
      { l: 'Rétro + angle mort gauche avant insertion', c: 'Contrôles' },
      { l: 'Voie extérieure tenue', c: 'Positionnement' },
      { l: 'Cligno droite sortie N-1', c: 'Signalisation sortie' },
      { l: 'Angle mort droit à la sortie', c: 'Contrôles sortie' },
      { l: 'Relance progressive 31 → 63 km/h', c: 'Allure sortie' },
      { l: 'Gestion boîte 1ère → 2ème → 3ème', c: 'Gestion boîte' },
    ],
    phrase: '« Quand c\'est saturé et qu\'un PL arrive, on attend qu\'il dégage toute la vue. »',
  },
};

const PORS = [
  { k: 'P — Prévoir', d: 'Panneau D42b 150 m + direction' },
  { k: 'O — Observer', d: 'Rétros + trafic anneau + vulnérables' },
  { k: 'R — Ralentir', d: '60→22 km/h + 2ème + cédez' },
  { k: 'S — S\'insérer', d: 'Voie ext., pas à côté PL, cligno sortie' },
];

const CHECKLIST = [
  { t: 'Position', d: 'voie de droite si sortie ≤ 2, sinon intérieur puis rabattement après N-1.' },
  { t: 'Allure', d: 'dégressive, pas d\'arrêt sauf trafic. 2ème à l\'entrée, 1ère si 0 km/h.' },
  { t: 'Observation', d: 'rétros + angle mort droit à la sortie.' },
  { t: 'Signalisation', d: 'pas de cligno à l\'entrée si tout droit, cligno droit après sortie précédente.' },
  { t: 'PL', d: 'laisser 1,5 voie, ne jamais doubler dans l\'anneau, distance > 1,5 m.' },
  { t: 'Moto', d: 'détecter, ne pas serrer, laisser se placer.' },
];

function SpeedCurve({ curve }) {
  const max = Math.max(...curve, 1);
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28" aria-hidden="true">
        {curve.map((v, i) => (
          <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-red-700 to-red-400 transition-all" style={{ height: `${Math.max(4, (v / max) * 100)}%`, opacity: 0.55 + 0.45 * (v / max) }} title={`${v} km/h`} />
        ))}
      </div>
      <div className="flex justify-between text-white/40 font-mono-spec text-[10px] mt-1.5">
        <span>début</span><span>entrée</span><span>anneau</span><span>sortie</span>
      </div>
    </div>
  );
}

const REGLES_OR = [
  { t: 'Priorité à l\'intérieur de l\'anneau', d: 'En giratoire français, tu cèdes à gauche à ceux déjà dedans. Pas de priorité à droite ici.', icon: CheckCircle2 },
  { t: 'Clignotant', d: 'Pas à l\'entrée. Cligno droit uniquement juste après la sortie qui précède la tienne.', icon: Clock },
  { t: 'Choix de voie', d: 'Extérieure si sortie directe (1ère/2ème). Intérieure si demi-tour ou 3/4 de tour, puis rabattement.', icon: Route },
  { t: 'Erreurs fréquentes', d: 'Couper la trajectoire du camion (angle mort énorme), coller une moto, s\'arrêter dans l\'anneau, sortie en 2ème file.', icon: AlertTriangle },
];

const PANNEAUX = [
  { k: 'Panneau A15', v: 'Annonce giratoire' },
  { k: 'Panneau AB25', v: 'Cédez-le-passage' },
  { k: 'Moto', v: 'Angles morts ×2', warn: true },
];

const CHECKLIST_ELEVE = [
  'J\'ai repéré A15 et AB25 (annonce + cédez)',
  'Je cède à gauche à ceux déjà dans l\'anneau',
  'Je mets le cligno droit après la sortie précédente',
  'Je contrôle rétro gauche + angle mort moto',
  'Je m\'engage voie extérieure, sans coller le camion',
];

function Quiz({ questions, storageKey }) {
  const [answers, setAnswers] = useState({});
  useEffect(() => { setAnswers({}); }, [storageKey]);
  const answered = Object.keys(answers).length;
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="giratoire-quiz">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <HelpCircle size={18} className="text-amber-400" /> Quiz interactif — ta vidéo
        </h3>
        <span className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[12px] font-mono-spec font-bold" data-testid="giratoire-quiz-score">{answered}/{questions.length} répondues</span>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {questions.map((qu, qi) => {
          const picked = answers[qi];
          return (
            <div key={qi} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="text-white text-[13px] font-bold leading-snug">Q{qi + 1} : {qu.q}</div>
                <span className="shrink-0 w-6 h-6 rounded-md bg-black/40 border border-white/10 text-white/50 font-mono-spec text-[10px] font-bold flex items-center justify-center">0{qi + 1}</span>
              </div>
              <div className="space-y-1.5">
                {qu.opts.map((o, oi) => {
                  const isPicked = picked === oi;
                  const showState = picked !== undefined;
                  const good = oi === qu.ok;
                  return (
                    <button key={oi} type="button" disabled={showState} data-testid={`giratoire-quiz-q${qi}-o${oi}`}
                      onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`w-full flex items-start gap-2 rounded-lg border px-2.5 py-2 text-left text-[12px] leading-snug transition ${
                        showState && good ? 'border-emerald-600/50 bg-emerald-950/30 text-emerald-200'
                        : showState && isPicked ? 'border-red-600/50 bg-red-950/30 text-red-300'
                        : showState ? 'border-white/5 bg-transparent text-white/35'
                        : 'border-white/10 bg-white/[0.02] text-white/75 hover:bg-white/[0.06]'}`}>
                      <span className="shrink-0 w-4.5 h-4.5 w-[18px] h-[18px] rounded-full border border-current flex items-center justify-center text-[9.5px] font-bold">{String.fromCharCode(65 + oi)}</span>
                      {o}
                    </button>
                  );
                })}
              </div>
              {picked !== undefined && (
                <p className={`mt-2.5 text-[11.5px] leading-snug ${picked === qu.ok ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {picked === qu.ok ? '✓ Bonne réponse. ' : '✗ Pas tout à fait. '}{qu.exp}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistEleve() {
  const KEY = 'cv-giratoire-checklist-eleve';
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
  });
  const n = CHECKLIST_ELEVE.filter((_, i) => done[i]).length;
  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="giratoire-checklist-eleve">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-400" /> Check-list élève — avant prochain giratoire
        </h3>
        <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${n === CHECKLIST_ELEVE.length ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="giratoire-checklist-score">{n}/{CHECKLIST_ELEVE.length} validés</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {CHECKLIST_ELEVE.map((c, i) => (
          <button key={i} type="button" onClick={() => toggle(i)} data-testid={`giratoire-checklist-${i}`}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${done[i] ? 'border-emerald-600/40 bg-emerald-950/20' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${done[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25'}`}>
              {done[i] && <CheckCircle2 size={14} className="text-black" />}
            </span>
            <span className={`text-[13px] leading-snug ${done[i] ? 'text-white' : 'text-white/70'}`}>{c}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 text-[11px] font-semibold">Méthode validée par moniteur</span>
        <span className="px-3 py-1.5 rounded-full bg-amber-950/20 border border-amber-700/30 text-amber-300 text-[11px] font-semibold">Astuce : refais la timeline commentée 2×</span>
      </div>
    </div>
  );
}

function VideoPlayer({ src, chapters, title, dangerNote }) {
  const ref = React.useRef(null);
  const [available, setAvailable] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => { setAvailable(true); setActive(0); }, [src]);

  const jump = (t, i) => {
    setActive(i);
    const v = ref.current;
    if (v) { v.currentTime = t; v.play().catch(() => {}); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5" data-testid="giratoire-video-player">
      <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5">
        <Film size={13} className="text-red-400" /> Vidéo embarquée · {title}
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="rounded-xl overflow-hidden bg-black border border-white/10 aspect-video relative">
          {available ? (
            <video
              ref={ref}
              src={src}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain"
              onError={() => setAvailable(false)}
              onTimeUpdate={(e) => {
                const ct = e.target.currentTime;
                let idx = 0;
                chapters.forEach((c, i) => { if (ct >= c.t) idx = i; });
                setActive(idx);
              }}
              data-testid="giratoire-video-element"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="giratoire-video-placeholder">
              <Film size={28} className="text-white/25 mb-3" />
              <div className="text-white/70 text-[13.5px] font-bold">Vidéo en cours d'ajout</div>
              <p className="text-white/40 text-[12px] mt-1.5 leading-relaxed max-w-xs">
                La fiche fonctionne déjà avec les timecodes ci-dessous. La vidéo réelle (plaques floutées) sera affichée ici dès son intégration.
              </p>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">Chapitres cliquables</div>
          {chapters.map((c, i) => (
            <button key={i} type="button" onClick={() => jump(c.t, i)} data-testid={`giratoire-chapter-${i}`}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${active === i && available ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
              <span className="w-10 shrink-0 text-center font-mono-spec text-[11px] font-bold text-red-300 bg-black/40 border border-white/10 rounded-md py-1">
                {String(Math.floor(c.t / 60)).padStart(1, '0')}:{String(c.t % 60).padStart(2, '0')}
              </span>
              <span className="flex-1">
                <span className="block text-white/90 text-[12.5px] font-bold leading-tight">{c.label}</span>
                <span className="block text-white/45 text-[11px] mt-0.5 leading-tight">{c.desc}</span>
              </span>
            </button>
          ))}
          {dangerNote && (
            <div className="rounded-xl bg-amber-950/20 border border-amber-700/30 p-3 mt-2">
              <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={11} /> Danger</div>
              <p className="text-white/70 text-[11.5px] leading-snug">{dangerNote}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AnalyseGiratoirePage() {
  const [vid, setVid] = useState('pl-moto');
  const [seg, setSeg] = useState(0);
  const [checked, setChecked] = useState({});
  const [showCorrection, setShowCorrection] = useState(false);
  const data = VIDEOS[vid];
  const lsKey = `cv-giratoire-grille-${vid}`;

  useEffect(() => {
    try { setChecked(JSON.parse(localStorage.getItem(lsKey)) || {}); } catch { setChecked({}); }
    setSeg(0);
  }, [lsKey]);

  const nbChecked = useMemo(() => data.grille.filter((_, i) => checked[i]).length, [checked, data]);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem(lsKey, JSON.stringify(next));
  };
  const reset = () => { setChecked({}); localStorage.removeItem(lsKey); };

  const s = data.segments[seg];

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12 pb-16" data-testid="analyse-giratoire-page">
        <FadeUp>
          <SectionTitle
            kicker="Analyse vidéo commentée · REMC C2"
            title="Le giratoire hors agglo, décodé seconde par seconde."
            sub="Deux situations réelles analysées par un moniteur ECSR : timeline pédagogique à timecodes, pièges à éviter, et grille de révision à cocher pour vérifier que tu es prêt."
          />
        </FadeUp>

        {/* Onglets vidéos */}
        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(VIDEOS).map(([key, v]) => (
            <button key={key} type="button" onClick={() => setVid(key)} data-testid={`giratoire-tab-${key}`}
              className={`h-11 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 border transition ${
                vid === key ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-900/40' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'
              }`}>
              <Film size={14} /> {v.tab}
            </button>
          ))}
        </div>

        {/* Lecteur vidéo + chapitres */}
        <VideoPlayer src={data.videoSrc} chapters={data.chapters} title={data.tab} dangerNote={data.dangerNote} />

        {/* Hero fiche */}
        <div className="mt-5 grid lg:grid-cols-[1.5fr_1fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-3">
              <Clock size={11} /> {data.badge}
            </div>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] leading-tight">{data.titre}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.identite.chips.map((c, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[11.5px]">{c}</span>
              ))}
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {[
                { k: 'Type de giratoire', v: data.identite.type, d: data.identite.typeDesc, cl: 'border-white/10' },
                { k: 'Difficulté', v: data.identite.difficulte, d: data.identite.difficulteDesc, cl: 'border-amber-600/30' },
                { k: 'Danger principal', v: data.identite.danger, d: data.identite.dangerDesc, cl: 'border-red-700/40' },
              ].map((c, i) => (
                <div key={i} className={`rounded-xl bg-white/[0.03] border ${c.cl} p-3.5`}>
                  <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">{c.k}</div>
                  <div className="text-white text-[13.5px] font-bold mt-1 leading-snug">{c.v}</div>
                  <div className="text-white/50 text-[11.5px] mt-1 leading-snug">{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-red-900/40 bg-gradient-to-br from-[#1a0b0d] to-[#0d0d0e] p-5 sm:p-6 flex flex-col">
            <div className="text-white/45 text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Gauge size={12} className="text-red-400" /> Courbe d'allure relevée</div>
            <SpeedCurve curve={data.curve} />
            <div className="mt-4 grid grid-cols-3 gap-2">
              {data.stats.map((st, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/10 p-2.5 text-center">
                  <div className="font-serif-editorial text-red-400 text-[24px] leading-none">{st.v}</div>
                  <div className="text-white/50 text-[9.5px] uppercase tracking-wider font-mono-spec mt-1">{st.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-3.5 text-[12.5px] text-white/75 leading-relaxed">
              <div className="text-red-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5">Ce que doit comprendre l'élève</div>
              {data.comprendre}
              <div className="mt-2.5 flex gap-2">
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">Vitesse légale {data.vitesseLegale}</span>
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">{data.rapport}</span>
              </div>
              <div className="text-white/35 font-mono-spec text-[10px] mt-2.5">{data.remc}</div>
            </div>
          </div>
        </div>

        {/* Focus pédagogique */}
        <div className="mt-5 rounded-2xl border border-white/15 bg-gradient-to-r from-[#15151a] to-[#0d0d0e] p-4 sm:p-5 flex items-start gap-3" data-testid="giratoire-focus">
          <span className="shrink-0 w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center"><AlertTriangle size={16} /></span>
          <div>
            <p className="text-white/80 text-[13px] leading-relaxed"><strong className="text-amber-300">Focus pédagogique :</strong> {data.focus.txt}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.focus.tags.map((t, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/60 text-[10.5px] font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline interactive */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px]">Timeline pédagogique interactive</h3>
            <span className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec font-bold">4 timecodes cliquables</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {data.segments.map((sg, i) => (
              <button key={i} type="button" onClick={() => setSeg(i)} data-testid={`giratoire-seg-${i}`}
                className={`rounded-xl border p-3 text-left transition ${seg === i ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
                <div className="font-mono-spec text-[10.5px] text-white/50">{sg.time}</div>
                <div className={`text-[13px] font-bold mt-1 leading-tight ${seg === i ? 'text-red-300' : 'text-white/85'}`}>{sg.emoji} {sg.title}</div>
                <div className="font-mono-spec text-[10.5px] text-white/45 mt-1">{sg.speed}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid lg:grid-cols-[1.3fr_1fr] gap-4">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 sm:p-5">
              <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Eye size={12} className="text-red-400" /> Ce qu'on observe · {s.time}</div>
              <ul className="space-y-2">
                {s.obs.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13px] leading-snug">
                    <ChevronRight size={14} className="text-red-400 mt-0.5 shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
                <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5 inline-flex items-center gap-1.5"><HelpCircle size={12} /> Question moniteur</div>
                <div className="text-white text-[13.5px] font-bold">{s.q}</div>
                <p className="text-white/70 text-[12.5px] mt-2 leading-relaxed">{s.r}</p>
              </div>
              <div className="rounded-xl bg-emerald-950/15 border border-emerald-700/30 p-3.5">
                <div className="text-emerald-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Verdict allure</div>
                <p className="text-white/80 text-[12.5px] leading-snug">{s.verdict}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pièges */}
        <div className="mt-8">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] mb-4 inline-flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> Les 3 pièges de cette vidéo</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.pieges.map((p, i) => (
              <div key={i} className="rounded-xl border border-red-900/40 bg-[#120a0b] p-4">
                <div className="w-7 h-7 rounded-lg bg-red-600 text-white font-mono-spec text-[12px] font-bold flex items-center justify-center mb-3">{String(i + 1).padStart(2, '0')}</div>
                <div className="text-white text-[13.5px] font-bold leading-snug">{p.t}</div>
                <p className="text-white/60 text-[12px] mt-1.5 leading-relaxed">{p.d}</p>
                <span className="inline-block mt-2.5 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider">{p.tag}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-4 text-[12.5px] text-white/70 leading-relaxed">
            <strong className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-widest block mb-1">Le conducteur modèle</strong>
            {data.conduiteModele}
          </div>
        </div>

        {/* Règles d'or + cas du camion */}
        <div className="mt-8 grid lg:grid-cols-[1.5fr_1fr] gap-4" data-testid="giratoire-regles-or">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] mb-4">Règles d'or</h3>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {REGLES_OR.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center justify-center"><Icon size={15} /></span>
                    <div>
                      <div className="text-white text-[13.5px] font-bold leading-snug">{r.t}</div>
                      <p className="text-white/55 text-[12px] mt-1 leading-relaxed">{r.d}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {PANNEAUX.map((p, i) => (
                <div key={i} className={`rounded-lg border p-2.5 ${p.warn ? 'border-amber-700/40 bg-amber-950/15' : 'border-white/10 bg-white/[0.03]'}`}>
                  <div className={`text-[9.5px] uppercase tracking-widest font-mono-spec font-bold ${p.warn ? 'text-amber-300' : 'text-white/45'}`}>{p.k}</div>
                  <div className="text-white/85 text-[12px] font-bold mt-0.5">{p.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-blue-900/50 bg-gradient-to-br from-[#0e1526] to-[#0d0d0e] p-5 sm:p-6">
            <h3 className="font-serif-editorial text-white text-[19px] sm:text-[22px] inline-flex items-center gap-2 mb-3"><Truck size={18} className="text-blue-300" /> Le cas du camion</h3>
            <p className="text-white/70 text-[12.5px] leading-relaxed">
              Dans ta vidéo, le camion est engagé avant toi. Il prend large, son porte-à-faux balaye la voie extérieure.
              Ne jamais se mettre à sa droite dans l'anneau. Laisse-le sortir, puis engage-toi.
            </p>
            <div className="mt-4 rounded-xl bg-black/40 border border-white/10 p-3.5">
              <div className="text-blue-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Règle camion</div>
              <div className="text-white text-[13px] font-bold">1 camion = 1 anneau complet libre derrière lui</div>
            </div>
            <p className="text-white/40 text-[11px] mt-3">Idem pour moto : distance latérale 1 m mini.</p>
          </div>
        </div>

        {/* Quiz */}
        <Quiz questions={data.quiz} storageKey={vid} />

        {/* Grille de révision */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" id="grille">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
              <ClipboardCheck size={18} className="text-red-400" /> Grille de révision à cocher
            </h3>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${nbChecked >= 8 ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="giratoire-grille-score">
                {nbChecked}/{data.grille.length} validés
              </span>
              <button type="button" onClick={() => setShowCorrection(!showCorrection)} data-testid="giratoire-grille-correction"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                <Eye size={13} /> {showCorrection ? 'Masquer' : 'Voir'} correction moniteur
              </button>
              <button type="button" onClick={reset} data-testid="giratoire-grille-reset"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 text-[12px] font-semibold inline-flex items-center gap-1.5">
                <RotateCcw size={13} /> Réinitialiser
              </button>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {data.grille.map((g, i) => (
              <button key={i} type="button" onClick={() => toggle(i)} data-testid={`giratoire-critere-${i}`}
                className="w-full flex items-center gap-3 py-3 text-left group">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${checked[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25 group-hover:border-white/50'}`}>
                  {checked[i] && <CheckCircle2 size={14} className="text-black" />}
                </span>
                <span className={`flex-1 text-[13.5px] leading-snug transition ${checked[i] ? 'text-white' : 'text-white/70'}`}>{g.l}</span>
                {showCorrection && <span className="hidden sm:block text-white/40 text-[11px] italic shrink-0">{g.c} · cocher si validé</span>}
              </button>
            ))}
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#101b33] border border-blue-900/50 p-4">
              <div className="text-blue-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Seuil de validation</div>
              <p className="text-white/85 text-[12.5px] leading-snug">8/{data.grille.length} = entrée en autonomie possible. En dessous = refaire avec verbalisation.</p>
            </div>
            <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
              <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><Quote size={11} /> Phrase clé moniteur</div>
              <p className="text-white text-[13px] font-serif-editorial leading-snug">{data.phrase}</p>
            </div>
          </div>
        </div>

        {/* Check-list élève */}
        <ChecklistEleve />

        {/* Méthode PORS + checklist examen */}
        <div className="mt-8 grid lg:grid-cols-[1fr_1.3fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#14090b] to-[#0d0d0e] p-5 sm:p-6">
            <div className="text-red-300 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-2">Méthode P.O.R.S — Rappel giratoire</div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">Prévoir, Observer,<br />Ralentir, S'insérer.</h3>
            <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed">Méthode à réciter à l'oral avant chaque giratoire hors agglo.</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {PORS.map((p, i) => (
                <div key={i} className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
                  <div className="text-red-300 font-mono-spec text-[11px] font-bold uppercase tracking-wider">{p.k}</div>
                  <div className="text-white/65 text-[11.5px] mt-1 leading-snug">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6">
            <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><ListChecks size={13} className="text-red-400" /> Check-list examen — giratoire hors agglo</div>
            <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="text-[12.5px] text-white/70 leading-snug">
                  <strong className="text-white">{c.t}</strong> : {c.d}
                </li>
              ))}
            </ul>
            <div className="mt-4 inline-flex px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-white/55 font-mono-spec text-[10.5px]">Vidéo support : {data.badge}</div>
            <p className="text-white/35 text-[11px] mt-3">Fiche utilisable en salle, en voiture ou en autonomie · Sans impression · Code Voiture × moniteurs ECSR</p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
